import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IESDPCaptainReportsStudent,
  IESDPCreateTeams,
  IESDPFreeTimeGraph,
  IESDPIterationReview,
  IESDPTeamDocument,
  IESDPTeamIteration,
  IESDPTeamMaterials,
} from '@interfaces/IESDP';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ESDPChangesService {
  constructor(private _http: HttpClient) {}

  createTeams(
    data: IESDPCreateTeams[]
  ): Observable<{ group: number; course: number }[]> {
    const url = `${environment.baseUrl}api/v1/teams/create/`;
    return this._http.post<{ group: number; course: number }[]>(url, data);
  }

  createIteration(data: {
    team_id: number;
    start_date?: string;
    end_date?: string;
  }): Observable<IESDPTeamIteration> {
    const url = `${environment.baseUrl}api/v1/iterations/`;
    return this._http.post<IESDPTeamIteration>(url, data);
  }

  deleteIteration(iteration_id: number) {
    const url = `${environment.baseUrl}api/v1/iterations/${iteration_id}/`;
    return this._http.delete(url);
  }

  updateIterationTime(
    id: number,
    data: { start_date: string; end_date?: string }
  ): Observable<IESDPTeamIteration> {
    const url = `${environment.baseUrl}api/v1/iterations/${id}/`;
    return this._http.patch<IESDPTeamIteration>(url, data);
  }

  updateIterationRoles(
    id: number,
    data: { captain: number; analysts: number[] }
  ): Observable<IESDPTeamIteration> {
    const url = `${environment.baseUrl}api/v1/iterations/${id}/`;
    return this._http.put<IESDPTeamIteration>(url, data);
  }

  deleteStudentFromTeam(teamId: number, students: number[]) {
    const url = `${environment.baseUrl}api/v1/teams/${teamId}/students/`;
    return this._http.delete(url, { body: { users: students } });
  }

  addStudentsToTeam(teamId: number, students: number[]) {
    const url = `${environment.baseUrl}api/v1/teams/${teamId}/students/`;
    return this._http.post(url, { users: students });
  }

  transferStudentsToTeam(
    teamId: number,
    data: { team: string; users: number[] }
  ) {
    const url = `${environment.baseUrl}api/v1/teams/${teamId}/students/transfer/`;
    return this._http.post(url, data);
  }

  sendFreeTimeGraph(data: IESDPFreeTimeGraph) {
    const url = `${environment.baseUrl}api/v1/free-time-forms/${data.student_id}/`;
    return this._http.patch(url, { time_ranges: data.time_ranges });
  }

  freeTimeFormRemindLater() {
    const url = `${environment.baseUrl}api/v1/free-time-forms/remind-later/`;
    return this._http.post(url, {});
  }

  returnStudentsToTeam(teamId: number, students: number[]) {
    const url = `${environment.baseUrl}api/v1/teams/${teamId}/students/`;
    return this._http.patch(url, { users: students });
  }

  changeTeamMentor(teamId: number, mentors: number[]) {
    const url = `${environment.baseUrl}api/v1/teams/${teamId}/mentors/`;
    return this._http.patch(url, { users: mentors });
  }

  addTeamDocument(data: FormData): Observable<IESDPTeamIteration> {
    const url = `${environment.baseUrl}api/v1/team-documents/`;
    return this._http.post<IESDPTeamIteration>(url, data);
  }

  updateTeamDocument(
    data: { content: string; title: string },
    id: number
  ): Observable<IESDPTeamDocument> {
    const url = `${environment.baseUrl}api/v1/team-documents/${id}/`;
    return this._http.put<IESDPTeamDocument>(url, data);
  }

  deleteTeamDocument(id: number) {
    const url = `${environment.baseUrl}api/v1/team-documents/${id}/`;
    return this._http.delete(url);
  }

  activateESDP(groupId: number) {
    const url = `${environment.baseUrl}api/v1/groups/${groupId}/start-esdp/`;
    return this._http.post(url, {});
  }

  updateIterationReview(
    id: number,
    data: { text?: string; points?: string; is_published?: boolean }
  ): Observable<IESDPIterationReview> {
    const url = `${environment.baseUrl}api/v1/iteration-reviews/${id}/`;
    return this._http.put<IESDPIterationReview>(url, data);
  }

  updateIterationCaptainReports(
    iterationId: number,
    data: IESDPCaptainReportsStudent[]
  ): Observable<IESDPCaptainReportsStudent[]> {
    const url = `${environment.baseUrl}api/v1/iterations/${iterationId}/captain-reports/`;
    return this._http.put<IESDPCaptainReportsStudent[]>(url, data);
  }

  updateIterationStatus(id: number, data: { is_active: boolean }) {
    const url = `${environment.baseUrl}api/v1/iterations/${id}/status-change/`;
    return this._http.patch<IESDPCaptainReportsStudent[]>(url, data);
  }

  openAccessReviews(groupId: number) {
    const url = `${environment.baseUrl}api/v1/notes/final-reviews/`;
    return this._http.post(url, { group: groupId });
  }

  addHolidaysToTeam(data: { team: number; start_date: string; weeks: string }) {
    const url = `${environment.baseUrl}api/v1/team-holidays/`;
    return this._http.post(url, data);
  }

  updateTeam(teamId: number, data: { is_active?: boolean }) {
    const url = `${environment.baseUrl}api/v1/teams/${teamId}/`;
    return this._http.patch(url, data);
  }

  addTeamMaterial(
    data: FormData,
    team_id: number
  ): Observable<IESDPTeamMaterials> {
    const url = `${environment.baseUrl}api/v1/teams/${team_id}/files/`;
    return this._http.post<IESDPTeamMaterials>(url, data);
  }

  deleteTeamMaterial(id: number, fileId: number) {
    const url = `${environment.baseUrl}api/v1/teams/${id}/files/`;
    return this._http.delete(url, { body: { file_id: fileId } });
  }

  changeRoleToStudent(team_id: number, student_id: number, role: string) {
    const url = `${environment.baseUrl}api/v1/teams/${team_id}/students/assign_role/`;
    return this._http.post(url, { student: student_id, assigned_role: role });
  }
  deleteRoleToStudent(team_id: number, student_id: number, role: string) {
    const url = `${environment.baseUrl}api/v1/teams/${team_id}/students/assign_role_delete/`;
    return this._http.post(url, { student: student_id, assigned_role: role });
  }
}
